import React, { HTMLAttributes, forwardRef } from 'react'
import theme from 'presentation/styles/theme'
import DividerNew from 'presentation/shared/components/DividerNew'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import * as S from './style'

type SubCounter = {
  subCounterText: string | React.ReactElement
  subCounterValue?: number | string
  subCounterValueColor: keyof typeof theme.ds.colors
  subCounterPercentValue?: string
  subCounterPercentValueColor?: keyof typeof theme.ds.colors
  onClickSubCounter?: () => void
}

export type CardProps = {
  text: string | React.ReactElement
  countValue?: number | string
  icon?: React.ReactElement
  style?: React.CSSProperties
  shadow?: keyof typeof theme.ds.shadows
  radius?: keyof typeof theme.ds.typography.border.radius
  color?: keyof typeof theme.ds.colors
  countValueColor?: keyof typeof theme.ds.colors
  iconColor?: keyof typeof theme.ds.colors
  bgColor?: keyof typeof theme.ds.colors
  subCounters: SubCounter[]
  disabled?: boolean
  onClick?: () => void
  ref?: any
  highlight?: boolean
} & HTMLAttributes<HTMLDivElement>

const CardCounterMultiple = forwardRef(
  (
    {
      text,
      countValue,
      icon,
      style,
      shadow,
      radius,
      color = 'neutral700',
      countValueColor = 'neutral700',
      iconColor = 'neutral700',
      bgColor,
      subCounters,
      disabled,
      onClick,
      highlight = false,
      ...props
    }: CardProps,
    ref
  ) => {
    return (
      <S.CardWrapper
        style={style}
        shadow={shadow}
        radius={radius}
        bgColor={bgColor}
        disabled={disabled}
        highlight={highlight}
        ref={ref}
        {...props}
      >
        <S.CardHeader onClick={onClick} disabled={disabled}>
          {icon && (
            <>
              <S.IconWrapper iconColor={iconColor}>{icon}</S.IconWrapper>
              <DividerNew vertical style={{ marginRight: '16px' }} />
            </>
          )}
          {typeof text !== 'string' ? (
            text
          ) : (
            <HeadingNew
              as="h3"
              size="large"
              color={color}
              weight="semiBold"
              style={{
                marginLeft: 0,
                marginRight: 'auto',
                width: '100%',
                maxWidth: '300px'
              }}
            >
              {text}
            </HeadingNew>
          )}

          {!icon && (
            <DividerNew
              vertical
              style={{ marginLeft: 'auto', marginRight: 0 }}
            />
          )}

          {countValue !== undefined ? (
            <SupportTextNew
              color={countValueColor ?? color}
              size="xxlarge"
              weight="bold"
              style={{ marginRight: 0, marginLeft: '24px' }}
            >
              {countValue}
            </SupportTextNew>
          ) : null}
        </S.CardHeader>

        {subCounters && subCounters?.length > 0 && (
          <S.CardContent>
            {subCounters?.map((counter, index) => (
              <>
                <S.CardSubCounter
                  key={index}
                  onClick={
                    counter.onClickSubCounter && counter.onClickSubCounter
                  }
                >
                  {typeof counter.subCounterText !== 'string' ? (
                    counter.subCounterText
                  ) : (
                    <HeadingNew
                      as="h6"
                      color="neutral700"
                      size="xsmall"
                      weight="semiBold"
                    >
                      {counter.subCounterText}
                    </HeadingNew>
                  )}
                  <S.ValuesWrapper>
                    <SupportTextNew
                      color={counter.subCounterValueColor}
                      size="xxlarge"
                      weight="bold"
                      lineHeight="small"
                    >
                      {counter.subCounterValue ?? '-'}
                    </SupportTextNew>
                    {counter.subCounterPercentValue ? (
                      <>
                        <DividerNew vertical />
                        <SupportTextNew
                          color={counter.subCounterPercentValueColor}
                          size="xxlarge"
                          weight="bold"
                          lineHeight="small"
                        >
                          {counter.subCounterPercentValue ?? '-'}
                        </SupportTextNew>
                      </>
                    ) : null}
                  </S.ValuesWrapper>
                </S.CardSubCounter>
              </>
            ))}
          </S.CardContent>
        )}
      </S.CardWrapper>
    )
  }
)

CardCounterMultiple.displayName = 'CardCounterMultiple'
export default CardCounterMultiple
